import { useRef, useEffect,  useState } from "react"
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Webcam from 'react-webcam';
import { Box } from "@mui/material"

import { Inclination } from "./Inclination";
import { Tutorial } from "./Tutorial";
import { Countdown } from "./Countdown";
import { Results } from "./Results";

import { putS3, triggerMeasurement } from "../../../api/endpoints";
import { trackScanEvent } from "../../../telemetry";
import { Pose } from "./Pose";

import { check } from "./Pose/utils/interpretation"

import * as poseDetection from '@tensorflow-models/pose-detection';
import * as tf from '@tensorflow/tfjs';
import '@tensorflow/tfjs-backend-wasm';
//import '@mediapipe/pose';

const STEPS = {
    INCLINATION: "Inclination",
    FRONT: "Front",
    SIDE: "Side",
    POSE: "Pose",
    COUNTDOWN_FRONT: "Countdown Front",
    COUNTDOWN_SIDE: "Countdown Side",
    RESULTS: "Results"
}

export const Camera = ({
    restart, 
    previousScreen 
}) => {
    const { t } = useTranslation('components/all');

    const [searchParams, setSearchParams] = useSearchParams();
    const firstTutoSeen = useRef(false);
    const secondTutoSeen = useRef(false);

    const [step, setStep] = useState(STEPS.INCLINATION); // Change to STEPS.INCLINATION
    const stepNumber = useRef(0);

    const [screenHeight, setScreenHeight] = useState(window.innerHeight);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    const [videoConstraints, setVideoConstraints] = useState({ facingMode: "user" });

    const mid = searchParams.get("mid") || ""
    const gender = searchParams.get("gender") || "male"

    const [result, setResult] = useState("loading");

    const camRef = useRef(null)
    const scanFailed = useRef(false)

    const nextStep = () => {
        if (scanFailed.current) return;
        if (stepNumber.current === 0) {
            stepNumber.current = 1
            setStep(STEPS.FRONT)
        } else if (stepNumber.current === 1) {
            stepNumber.current = 2
            setStep(STEPS.POSE)
        } else if (stepNumber.current === 2) {
            stepNumber.current = 3
            setStep(STEPS.COUNTDOWN_FRONT)
        } else if (stepNumber.current === 3) {
            stepNumber.current = 4
            setStep(STEPS.SIDE)
        } else if (stepNumber.current === 4) {
            stepNumber.current = 5
            setStep(STEPS.POSE)
        } else if (stepNumber.current === 5) {
            stepNumber.current = 6
            setStep(STEPS.COUNTDOWN_SIDE)
        } else if (stepNumber.current === 6) {
            stepNumber.current = 7
            setStep(STEPS.RESULTS)
        }
    }

    useEffect(() => {
        const setupCamera = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter((d) => d.kind === "videoinput");
                
                const backCamera = videoDevices.find((d) =>
                    d.label.toLowerCase().includes("back") ||
                    d.label.toLowerCase().includes("rear")
                );
                const frontCamera = videoDevices.find((d) =>
                    d.label.toLowerCase().includes("front")
                );
                
                if (frontCamera) {
                    setVideoConstraints({ deviceId: frontCamera.deviceId });
                } else if (backCamera) {
                    setVideoConstraints({ deviceId: backCamera.deviceId });
                } else {
                    setVideoConstraints({ facingMode: "user" })
                }
            } catch (err) {
                console.warn("Could not access devices:", err);
                setVideoConstraints({ facingMode: "user" });
            }
        };
        
        setupCamera();
    }, []);

    useEffect(() => {
        console.log('stepNumber.current', stepNumber.current);
    }, [stepNumber.current])

    const getScreenshotWithRetry = async (attempts = 3) => {
        for (let i = 0; i < attempts; i++) {
            const img = camRef.current?.getScreenshot();
            if (img) return img;
            console.warn(`getScreenshot returned null (attempt ${i + 1}/${attempts})`);
            await new Promise((r) => setTimeout(r, 150));
        }
        return null;
    }

    const putS3WithRetry = async (type, imageSrc, attempts = 3) => {
        for (let i = 0; i < attempts; i++) {
            const ok = await putS3(type, imageSrc);
            if (ok) return true;
            console.warn(`putS3 '${type}' failed (attempt ${i + 1}/${attempts})`);
            await new Promise((r) => setTimeout(r, 600));
        }
        return false;
    }

    const isMeasurementSuccess = (res) => {
        if (!res) return false;
        if (typeof res === 'object') {
            if (typeof res.error === 'string') return false;
            if (res.statusCode !== undefined) {
                const code = Number(res.statusCode);
                if (!(code >= 200 && code < 300)) return false;
            }
        }
        return true;
    }

    const failScan = (reason) => {
        console.error('Body scan failed:', reason);
        trackScanEvent('scan-failed', { reason, stepNumber: stepNumber.current });
        scanFailed.current = true;
        stepNumber.current = 7;
        setResult("error");
        setStep(STEPS.RESULTS);
    }

    const takePhoto = async (type) => {
        if (!camRef.current) {
            failScan(`camera not ready when capturing '${type}'`);
            return false;
        }

        const imageSrc = await getScreenshotWithRetry();
        if (!imageSrc) {
            failScan(`empty screenshot when capturing '${type}'`);
            return false;
        }

        const uploaded = await putS3WithRetry(type, imageSrc);
        if (!uploaded) {
            failScan(`upload failed for '${type}'`);
            return false;
        }
        trackScanEvent('scan-photo-uploaded', { type });

        if (type === 'side') {
            try {
                const res = await triggerMeasurement(mid);
                if (isMeasurementSuccess(res)) {
                    trackScanEvent('scan-measurement-triggered', { success: true });
                    setResult("success");
                } else {
                    console.error('triggerMeasurement returned a failure response', res);
                    trackScanEvent('scan-measurement-triggered', { success: false });
                    setResult("error");
                }
            } catch (e) {
                console.log('Error : triggerMeasurement -', e);
                trackScanEvent('scan-measurement-triggered', { success: false, thrown: String(e?.message || e) });
                setResult("error");
            }
        }

        return true;
    }

    const [detector, setDetector] = useState(null)
    useEffect(() => {
        console.log('detector status', detector)
    }, [detector])

    const detectorCreated = useRef(false)
    const detectorCreating = useRef(false)
    const launchDetector = async () => {
        console.log('launchDetector started')
        try {
            detectorCreating.current = true
            await tf.ready();
            tf.engine().startScope()

            const model = poseDetection.SupportedModels.MoveNet
            const detectorConfig = {
                modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING
            }
            const detectorRes = await poseDetection.createDetector(model, detectorConfig);
            setDetector(detectorRes)
            detectorCreated.current = true
            trackScanEvent('scan-detector-created', { stepNumber: stepNumber.current })
        } catch (e) {
            console.log("Error when creating the detector:", e)
            trackScanEvent('scan-detector-error', { message: String(e?.message || e), stepNumber: stepNumber.current })
            detectorCreated.current = false
        }
        console.log("detectorCreated after creation", detectorCreated)
        detectorCreating.current = false
    }

    const createDetector = (location) => {
        console.log('createDetector attempt from', location)
        if ((!detector || !detectorCreated.current) && (!detectorCreating.current)) {
            console.log('createDetector launched')
            setTimeout(() => {
                if (!detectorCreated.current && !detectorCreating.current) {
                    launchDetector()
                }
            }, 500)
        } else {
            console.log('detector already created')
        }
    }

    const removeDetector = () => {
        try {
            if (detector) {
                detector.dispose()
                detectorCreated.current = false
                setDetector(null);
            }

            setDetector(null)
            detectorCreated.current = false

            if (tf && tf.disposeVariables) {
                tf.disposeVariables();
            } 
            tf.engine().endScope();
            console.log('memory after end', tf.memory())
            
        } catch (error) {
            console.log('removeDetector error', error)
        }
    }

    useEffect(() => {
        const handleResize = () => {
          console.log('handleSize')
          setScreenWidth(window.innerWidth);
          setScreenHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        console.log('camera step', step)
        trackScanEvent('scan-step-reached', { step, stepNumber: stepNumber.current })
    }, [step])

    useEffect(() => {
        console.log('detector', detector)
    }, [detector])

    const [fullPose, setFullPose] = useState()
    const [imageHeight, setImageHeight] = useState();
    const [imageWidth, setImageWidth] = useState();

    const estimatePosesLaunched = useRef(false)
    const estimatePoses = async () => {
        if (detector) {
            if (camRef?.current && camRef.current.video && camRef.current.video.readyState === 4) {
                const videoRef = camRef.current.video
                try {
                    const poses = await detector.estimatePoses(videoRef)
                    if (poses[0]) {
                        estimatePosesLaunched.current = true
                        setFullPose(poses[0].keypoints)
                        setImageHeight(videoRef.videoHeight)
                        setImageWidth(videoRef.videoWidth)
                    }
                } catch (error) {
                    estimatePosesLaunched.current = false
                    // Fires once per frame on a disposed detector: throttle hard
                    trackScanEvent('scan-estimate-pose-error', { message: String(error?.message || error) }, 10000)
                }

                requestAnimationFrame(estimatePoses);
            } else {
                setTimeout(estimatePoses, 200);
            }
        }
    }

    const launchEstimatePose = () => {
        if (!!(detector && camRef)) {
            console.log('launchEstimatePose passed')
            estimatePoses();
        }
    }

    const [comment, setComment] = useState(t('camera.index.loading'));
    const nextStepCalled = useRef({ "Front": false, "Side": false });

    const validateCoordinates = (data) => {
        const type = stepNumber.current === 2 ? STEPS.FRONT : stepNumber.current === 5 ? STEPS.SIDE : null
        const positionRes = check(data, type, imageHeight, imageWidth)
        if (positionRes !== null) {
            if (positionRes !== true) {
                setComment(positionRes)
            } else {
                setComment(null)
                if (!nextStepCalled.current[type]) {
                    nextStepCalled.current[type] = true;
                    nextStep('all good');
                }
            }
        } else {
            setComment(t('camera.index.poseComment'))
        }
    }

    useEffect(() => {
        if (fullPose) {
            validateCoordinates(fullPose)
        }
    }, [fullPose])

    useEffect(() => {
        if (!estimatePosesLaunched.current) {
            launchEstimatePose()
        }
    }, [detector])

    return (
        <Box sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
        }}>
            {step === STEPS.INCLINATION && (
                <Inclination
                    createDetector={() => createDetector('Inclination')}
                    previousScreen={previousScreen}
                    stepNumber={stepNumber.current}
                    startTutorial={nextStep}
                />
            )}
            {step === STEPS.FRONT && (
                <Tutorial
                    tutoSeen={firstTutoSeen}
                    setComment={setComment}
                    launchEstimatePose={launchEstimatePose}
                    createDetector={() => createDetector('Tuto 1')}
                    onEnd={nextStep}
                    gender={gender}
                    index={1}
                />
            )}
            {step === STEPS.SIDE && (
                <Tutorial 
                    tutoSeen={secondTutoSeen}
                    setComment={setComment}
                    launchEstimatePose={launchEstimatePose}
                    createDetector={() => createDetector('Tuto 2')}
                    onEnd={nextStep}
                    gender={gender}
                    index={2}
                />
            )}
            {step === STEPS.POSE && (
                <Pose
                    comment={comment}
                    createDetector={() => createDetector('Pose')}
                    detector={detector}
                    detectorCreated={detectorCreated}

                />
            )}
            {(step === STEPS.COUNTDOWN_FRONT || step === STEPS.COUNTDOWN_SIDE) && (
                <Countdown
                    takePhoto={takePhoto}
                    removeDetector={removeDetector}
                    end={step === STEPS.COUNTDOWN_SIDE}
                    nextStep={() => { nextStep('coutdown') }}
                />
            )}
            {step === STEPS.RESULTS && (
                <Results type={result} restart={restart} />
            )}
            <Webcam
                ref={camRef}
                mirrored={true}
                height={screenHeight}
                wdith={screenWidth}
                screenshotFormat="image/png"
                style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}
                videoConstraints={{
                    ...videoConstraints,
                    aspectRatio: screenHeight / screenWidth,
                }}
                onUserMediaError={(err) => {
                    console.error("Camera access denied:", err);
                    trackScanEvent('scan-camera-denied', { error: String(err?.name || err) });
                    alert("Camera access is blocked. Please check your permissions or try another browser.");
                    setTimeout(() => window.close(), 2000);
                }}
            />
        </Box>
    )
}
